import axios from 'axios';
import api from '../../utils/axios';
import { enAU } from 'date-fns/locale';
import { 
  VentaResponse, 
  VentaTransformada, 
  ArticuloVendidoResponse, 
  ArticuloVenta, 
  ImpuestoVentaResponse
} from '../../components/types/ventas.types';

const API_URL = process.env.REACT_APP_API_URL || '';

interface DevolucionArticulo {
  idArticulo: number;
  cantidad: number;
}

interface ProcesarDevolucionParams {
  idVenta: number;
  articulos: DevolucionArticulo[];
}

interface DevolucionArticulo {
  idArticulo: number;
  cantidad: number;
  idMotivo?: number;
  observacion?: string;
}

interface CreateDevolucionVentaDto {
  codigoFactura: string;
  articulos: Array<{
    idArticulo: number;
    cantidad: number;
    idMotivo?: number;
    observacion?: string;
  }>;
}



export class VentasApi {
  static async crearVenta(idCliente: number, idUsuario: number, fechaVenta: string) {
    try {
      const response = await api.post(`${API_URL}/ventas/crear`, {
        IdCliente: idCliente,
        IdUsuario: idUsuario,
        FechaVenta: fechaVenta
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al crear la venta');
    }
  }

  static async ajustarStock(params: {
    idVenta: number;
    idArticulo: number;
    cantidad: number;
    accion: 'AddAll' | 'AddArt' | 'DelAll' | 'DelArt';
  }) {
    try {
      const response = await api.post(`${API_URL}/ventas/ajustar-stock`, params);
      return response.data;
    } catch (error) {
      throw new Error('Error al ajustar el stock');
    }
  }

  // src/services/VentasApi.ts

  static async irAPagar(params: { idVenta: number; idSucursal: number, usarFacturaOficial: boolean }) {
    try {
      const response = await api.post(`${API_URL}/ventas/ir-a-pagar`, params);

      if (!response.data) {
        throw new Error('No se recibió respuesta del servidor');
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Error al procesar el pago');
      }
      throw new Error('Error de conexión al servidor');
    }
  }


  static async cobrarFactura(params: {
    IdVenta: number;
    IdTipoPago: number;
    Abono: number;
    Observacion: string;
    CreditoInfo?: { NumeroCuotas: number; FechaPrimeraCuota: string; IntervaloDias: number };
  }) {
    try {
      const response = await api.post(`${API_URL}/ventas/cobrar`, params);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Error al cobrar la factura');
      }
      throw new Error('Error al cobrar la factura');
    }
  }

  /*   static async cobrarFactura(params: {
      IdVenta: number;
      IdTipoPago: number;
      Abono: number;
      Observacion: string;
    }) {
      try {
        const response = await api.post(`${API_URL}/ventas/cobrar`, params);
        return response.data;
      } catch (error) {
        throw new Error('Error al cobrar la factura');
      }
    } */

  // src/services/VentasApi.ts
  static async cancelarVenta(ventaId: number, tipo: 'cancelacion' | 'anulacion', observacion?: string, codigoAutorizacion?: string) {
    try {
      const response = await api.post(`${API_URL}/ventas/cancelar`, {
        Id: ventaId,
        tipo,
        observacion,
        ...(codigoAutorizacion ? { codigoAutorizacion } : {}),
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        throw new Error(Array.isArray(error.response.data.message)
          ? error.response.data.message[0]
          : error.response.data.message);
      }
      throw new Error('Error al cancelar la venta');
    }
  }

  static async buscarClientes(termino: string) {
    try {
      const response = await api.post(`${API_URL}/clientes/buscar`, { termino });
      return response.data;
    } catch (error) {
      throw new Error('Error al buscar clientes');
    }
  }

  static async buscarArticulos(termino: string) {
    try {
      const response = await api.post(`${API_URL}/articulos/buscar`, { termino });
      return response.data;
    } catch (error) {
      throw new Error('Error al buscar artículos');
    }
  }

  static async getVentas() {
    try {   
      const response = await api.post(`${API_URL}/ventas/obtener-todas`);
      
      const ventas = response.data.map((venta: any) => {
        // Primero agrupamos los artículos por EAN
        const articulosAgrupados = venta.articulosVendidos.reduce((acc: any, art: any) => {
          const key = art.EAN;
          if (!acc[key]) {
            acc[key] = {
              ...art,
              cantidad: 0,
              subtotalSinDescuento: 0,
              subtotalConDescuento: 0
            };
          }
          acc[key].cantidad += art.cantidad;
          return acc;
        }, {});

        // Transformamos el objeto agrupado en un array
        const articulosMapeados = Object.values(articulosAgrupados).map((art: any) => {
          const articuloTransformado = {
            Id: 0,
            Nombre: art.nombreArticulo,
            EAN: art.EAN,
            PrecioVenta: art.PrecioVenta,
            Cantidad: art.cantidad,
            Descuento: art.descuento,
            Valor: 0,
            stock: 0,
            Impuesto: [{
              Id: 1,
              Nombre: 'IVA',
              Valor: 19
            }]
          };

          // Calcular el valor con el precio correcto y cantidades agrupadas
          const subtotalSinDescuento = articuloTransformado.PrecioVenta * articuloTransformado.Cantidad;
          const descuento = subtotalSinDescuento * (articuloTransformado.Descuento / 100);
          articuloTransformado.Valor = subtotalSinDescuento - descuento;

          return articuloTransformado;
        });

        // El resto del mapeo sigue igual
        return {
          id: venta.informacionVenta.id || 0,
          fecha: venta.informacionVenta.fechaVenta,
          numeroFactura: venta.informacionVenta.codigoFacturaVenta,
          cliente: {
            id: venta.informacionVenta.identificacionCliente,
            nombre: venta.informacionVenta.nombreCliente,
            identificacion: venta.informacionVenta.identificacionCliente,
            direccion: venta.informacionVenta.direccionCliente,
            telefono: venta.informacionVenta.telefonoCliente,
            correo: venta.informacionVenta.correoCliente || '',
          },
          sucursal: {
            id: 0,
            nombre: venta.articulosVendidos[0]?.nombreSucursal
          },
          vendedor: {
            id: 0,
            nombre: venta.informacionVenta.nombreUsuario
          },
          articulos: articulosMapeados,
          subtotal: venta.detalleVenta.subtotal,
          descuentoTotal: articulosMapeados.reduce((total: number, art: any) => {
            const subtotalArticulo = art.PrecioVenta * art.Cantidad;
            return total + (subtotalArticulo * (art.Descuento / 100));
          }, 0),
          impuestos: venta.impuestos.map((imp: any) => ({
            nombre: imp.nombreImpuesto,
            valor: typeof imp.valorImpuesto === 'string' ? 
              parseFloat(imp.valorImpuesto) : imp.valorImpuesto
          })),
          totalAPagar: venta.detalleVenta.total,
          estado: this.mapearEstado(venta.informacionVenta.estadoVenta),
          tipoPago: {
            id: 0,
            nombre: venta.detalleVenta.tipoPago
          },
          observaciones: venta.detalleVenta.observacion
        };
      });

      return ventas;
    } catch (error) {
      console.error('Error en getVentas:', error);
      throw error;
    }
}

// Agregar estos métodos auxiliares
private static calcularSubtotal(articulos: any[]): number {
    return articulos.reduce((total, art) => {
        const precio = art.PrecioVenta || 0;
        const cantidad = art.cantidad || 0;
        return total + (precio * cantidad);
    }, 0);
}

private static calcularDescuentoTotal(articulos: any[]): number {
    return articulos.reduce((total, art) => {
        const subtotalArticulo = (art.PrecioVenta || 0) * (art.cantidad || 0);
        const descuentoPorcentaje = (art.descuento || 0) / 100;
        return total + (subtotalArticulo * descuentoPorcentaje);
    }, 0);
}

  // Método auxiliar para mapear estados
  private static mapearEstado(estadoId: number): string {
    const estados: { [key: number]: string } = {
      1: 'Abierta',
      2: 'Pendiente',
      3: 'Anulada',
      4: 'Pagada',
      5: 'Crédito',
    };
    return estados[estadoId] || 'desconocido';
  }


  static async getVentaById(numeroFactura: string): Promise<VentaTransformada> {
    try {    
      const response = await api.post<VentaResponse>(`${API_URL}/ventas/informacion`, {
        codigoFactura: numeroFactura
      });

      if (!response.data) {
        throw new Error('No se recibieron datos del servidor');
      }

      const venta = response.data;
      const subtotal = this.calcularSubtotal(venta.articulosVendidos);
      const descuentoTotal = this.calcularDescuentoTotal(venta.articulosVendidos);

      return {
        id: venta.informacionVenta?.id || 0,
        numeroFactura: venta.informacionVenta?.codigoFacturaVenta || numeroFactura,
        cliente: {
          id: Number(venta.informacionVenta?.identificacionCliente) || 0,
          nombre: venta.informacionVenta?.nombreCliente || '',
          identificacion: venta.informacionVenta?.identificacionCliente || ''
        },
        totalAPagar: venta.detalleVenta?.total || 0,
        saldoPendiente: venta.detalleVenta?.saldo || 0,
        subtotal,
        descuentoTotal,
        estado: this.mapearEstado(venta.informacionVenta?.estadoVenta || 2),
        tipoPago: {
          id: venta.detalleVenta?.idTipoPago || 1,
          nombre: venta.detalleVenta?.tipoPago || 'EFECTIVO'
        },
        fecha: venta.informacionVenta?.fechaVenta || new Date().toISOString(),
        observaciones: venta.detalleVenta?.observacion || '',
        articulos: this.transformarArticulos(venta.articulosVendidos),
        impuestos: this.transformarImpuestos(venta.impuestos)
      };
    } catch (error) {
      console.error("VentasApi: Error en getVentaById", error);
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        throw new Error('Error al obtener la información de la venta: ' + error.response.data.message);
      }
      throw new Error('Error de conexión al servidor');
    }
  }

  private static transformarArticulos(articulos: ArticuloVendidoResponse[]): ArticuloVenta[] {
    if (!Array.isArray(articulos)) return [];
    
    return articulos.map(art => ({
      Id: 0, // Generamos un ID temporal si es necesario
      Nombre: art.nombreArticulo || '',
      EAN: art.EAN || '',
      PrecioVenta: art.PrecioVenta || art.precioVenta ||  0,
      Cantidad: art.cantidad || 0,
      Descuento: art.descuento || 0,
      Valor: (art.precioVenta || 0) * (art.cantidad || 0),
      stock: art.stock || 0,
      Impuesto: [] // Array vacío por defecto
    }));
  }

  private static transformarImpuestos(impuestos: VentaResponse['impuestos']) {
    if (!Array.isArray(impuestos)) return [];
    
    return impuestos.map(imp => ({
      nombre: imp.nombreImpuesto,
      valor: typeof imp.valorImpuesto === 'string' ? 
        parseFloat(imp.valorImpuesto) : (imp.valorImpuesto || 0)
    }));
  }

  static async buscarVentaParaDevolucion(params: {
    tipo: 'factura' | 'cliente';
    termino: string;
  }) {
    try {
      const response = await api.post(`${API_URL}/devoluciones-venta/buscar-devolucion`, params);
      
      // Transformamos la respuesta al formato esperado por el componente
      return {
        informacionVenta: {
          id: response.data.id,
          codigoFacturaVenta: response.data.codigoFactura,
          estadoVenta: response.data.estadoVenta,
          fechaVenta: response.data.fechaVenta,
          nombreCliente: response.data.cliente.nombre,
          identificacionCliente: response.data.cliente.identificacion
        },
        articulosVendidos: response.data.articulosVendidos
      };
    } catch (error) {
      console.error('Error en buscarVentaParaDevolucion:', error);
      throw new Error('Error al buscar la venta');
    }
  }

  static async procesarDevolucion(params: CreateDevolucionVentaDto): Promise<any> {
    try {
      // Enviamos el DTO exactamente como lo espera el backend
      const payload: CreateDevolucionVentaDto = {
        codigoFactura: params.codigoFactura,
        articulos: params.articulos
      };
  
      const response = await api.post(`${API_URL}/devoluciones-venta/procesar`, payload);
      return response.data;
    } catch (error) {
      console.error('Error en procesarDevolucion:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Error al procesar la devolución');
      }
      throw new Error('Error al procesar la devolución');
    }
  }

  static async getVentaEnProceso(idVenta: number) {
    try {
      const response = await api.post(`${API_URL}/ventas/en-proceso`, { idVenta });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Error al obtener la venta en proceso');
      }
      throw error;
    }
  }

  static async cancelarVentaEnProceso(idVenta: number) {
    try {
      const response = await api.post(`${API_URL}/ventas/cancelar-proceso`, {
        idVenta
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Error al cancelar la venta');
      }
      throw error;
    }
  }

  /** Devuelve { idVenta: cuotasVencidas } para todas las ventas crédito con cuotas vencidas */
  static async getCuotasVencidasPorVenta(): Promise<Record<number, number>> {
    try {
      const response = await api.get(`${API_URL}/ventas/credito/cuotas-vencidas-resumen`);
      return response.data;
    } catch {
      return {};
    }
  }

  static async enviarFacturaPorEmail(params: {
    codigoFactura: string;
    emailDestino?: string;
  }): Promise<{ mensaje: string }> {
    try {
      const response = await api.post(`${API_URL}/ventas/enviar-email`, params);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Error al enviar la factura por correo');
      }
      throw new Error('Error al enviar la factura por correo');
    }
  }

  static async ajustarStockEnProceso(params: {
    idVenta: number;
    idArticulo: number;
    idSucursal: number;
    cantidad: number;
    accion: 'AddAll' | 'AddArt' | 'DelAll' | 'DelArt';
  }) {
    try {
      const response = await api.post(
        `${API_URL}/ventas/ajustar-stock-proceso`,
        params
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Error al ajustar el stock');
      }
      throw error;
    }
  }
 /*  private static calcularSubtotal(articulos: ArticuloVendidoResponse[]): number {
    if (!Array.isArray(articulos)) return 0;
    
    return articulos.reduce((total, art) => 
      total + ((art.precioVenta || 0) * (art.cantidad || 0)), 0);
  }

  private static calcularDescuentoTotal(articulos: ArticuloVendidoResponse[]): number {
    if (!Array.isArray(articulos)) return 0;
    
    return articulos.reduce((total, art) => {
      const subtotalArticulo = (art.precioVenta || 0) * (art.cantidad || 0);
      const descuentoPorcentaje = (art.descuento || 0) / 100;
      return total + (subtotalArticulo * descuentoPorcentaje);
    }, 0);
  } */

  /* private static mapearEstado(estadoId: number): string {
    const estados: { [key: number]: string } = {
      1: 'Abierta',
      2: 'Pendiente',
      3: 'Anulada',
      4: 'Pagada'
    };
    return estados[estadoId] || 'desconocido';
  } */
}